import React, { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import {
  Container, Row, Col,
  Card, CardHeader, CardBody,
  ListGroup, ListGroupItem,
  Nav, NavItem, NavLink, TabContent, TabPane,
  Button, Modal, ModalHeader, ModalBody, ModalFooter,
  Form, FormGroup, Label, Input,
  InputGroup, InputGroupText,
  Table, Spinner, Alert, Badge
} from "reactstrap";
import "./HospitalSubscriptions.ui.css";

/* ---------------- helpers ---------------- */
const formatDate = (d) => {
  if (!d) return "—";

  // MySQL DATE values represent a calendar date, not a UTC timestamp.
  // Parse them locally so users west of UTC do not see the previous day.
  const dateOnlyMatch = typeof d === "string"
    ? d.trim().match(/^(\d{4})-(\d{2})-(\d{2})(?:T00:00:00(?:\.000)?Z)?$/)
    : null;
  const dt = dateOnlyMatch
    ? new Date(Number(dateOnlyMatch[1]), Number(dateOnlyMatch[2]) - 1, Number(dateOnlyMatch[3]))
    : (typeof d === "string" ? new Date(d) : d);
  if (isNaN(dt.getTime())) return "—";
  return dt.toLocaleDateString("de-DE", { year: "numeric", month: "short", day: "2-digit" });
};
const formatCurrency = (v) => {
  const n = typeof v === "string" ? Number(v) : v ?? 0;
  return new Intl.NumberFormat(undefined, { style: "currency", currency: "EUR", maximumFractionDigits: 2 }).format(n);
};
const formatCurrencyOrDash = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  return formatCurrency(v);
};
const formatFileName = (path) => {
  if (!path) return "";
  const segments = String(path).split("/");
  return segments[segments.length - 1] || path;
};
const formatPercent = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  const num = Number(v);
  if (isNaN(num)) return String(v);
  return `${num}%`;
};
const formatPaymentPlan = (plan) => {
  if (!plan) return "—";
  const normalized = String(plan).trim().toLowerCase();
  if (["monthly", "monatlich", "month"].includes(normalized)) return "Monatlich";
  if (["annual", "jährlich", "jaehrlich", "yearly"].includes(normalized)) return "Jährlich";
  if (["quarterly", "vierteljährlich", "quarter"].includes(normalized)) return "Quartal";
  return plan;
};

/** Normalize raw status (EN/DE, mixed/typo variants) to a canonical key */
const normalizeStatus = (status) => {
  const s = String(status || "").trim().toLowerCase();
  const re = (r) => r.test(s);

  if (re(/deact|inaktiv|deaktiv|deaktiviert|suspend|disable/)) return "deactive";
  if (re(/cancel|gekü|geku|künd|kuend/))                     return "canceled";
  if (re(/pending|wartend|aussteh|geplant|planned|schedule|terminiert|vorgemerkt/)) return "pending"; // ← NEW aliases
  if (re(/überfäll|ueberfaell|ueberfäll|overdue/))           return "overdue";
  if (re(/unbez|unpaid/))                                    return "unpaid";
  if (re(/bezahlt|paid/))                                    return "paid";
  if (re(/trial|test|probe/))                                return "trial";
  if (re(/aktiv|active/))                                    return "active";
  return "unknown";
};


/** German label for a canonical status key */
const statusLabelDE = (key) =>
  ({
    active: "Aktiv",
    deactive: "Deaktiv",
    canceled: "Gekündigt",
    trial: "Testphase",
    paid: "Bezahlt",
    unpaid: "Unbezahlt",
    overdue: "Überfällig",
    unknown: "Unbekannt",
     pending: "Geplant",   
  }[key] || "Unbekannt");

/** High-contrast badge with German label, independent of theme */
const StatusBadge = ({ status }) => {
  const key = normalizeStatus(status);
  const label = statusLabelDE(key);

  const styles = {
    active:   { bg: "#198754", fg: "#fff" },     // grün
    trial:    { bg: "#ffc107", fg: "#212529" },  // gelb + dunkler Text
    canceled: { bg: "#dc3545", fg: "#fff" },     // rot
    deactive: { bg: "#6c757d", fg: "#fff" },     // dunkelgrau
    overdue:  { bg: "#dc3545", fg: "#fff" },     // rot
    unpaid:   { bg: "#ffc107", fg: "#212529" },  // gelb
    paid:     { bg: "#198754", fg: "#fff" },     // grün
    unknown:  { bg: "#adb5bd", fg: "#212529" },  // grau
      pending:  { bg: "#0d6efd", fg: "#fff" },   // NEW (blue)

  }[key];

  return (
    <span
      className="badge rounded-pill"
      style={{ backgroundColor: styles.bg, color: styles.fg }}
    >
      {label}
    </span>
  );
};

/* ------------- component ------------- */
const CompanySubscriptions = () => {
  // left list state
  const [hospitals, setHospitals] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [listError, setListError] = useState(null);
  const [query, setQuery] = useState("");
  const [perPage, setPerPage] = useState(20);

  // selection
  const [selected, setSelected] = useState(null);

  // detail state
  const [activeTab, setActiveTab] = useState("1");
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [detailError, setDetailError] = useState(null);
  const [detailSuccess, setDetailSuccess] = useState(null);

  // subscriptions & invoices
  const [subscriptions, setSubscriptions] = useState([]); // plural
  const [invoices, setInvoices] = useState([]);

  // create invoice modal
  const [invoiceModal, setInvoiceModal] = useState(false);
  const [invoiceForm, setInvoiceForm] = useState({
    amount: "",
    dueDate: "",
    description: ""
  });

  // invoice sorting
  const [invoiceSort, setInvoiceSort] = useState({ key: "due_date", dir: "desc" });

  // per-row action loaders
  const [updatingSubId, setUpdatingSubId] = useState(null);
  const [sendingReminderId, setSendingReminderId] = useState(null);
  const [updatingInvoiceId, setUpdatingInvoiceId] = useState(null);
  const [downloadingInvoiceId, setDownloadingInvoiceId] = useState(null);
  const [downloadingContractId, setDownloadingContractId] = useState(null);
  const [overrides, setOverrides] = useState([]);
  const [loadingOverrides, setLoadingOverrides] = useState(false);
  const [savingOverride, setSavingOverride] = useState(false);
  const [editingOverrideId, setEditingOverrideId] = useState(null);
  const [overridePreview, setOverridePreview] = useState(null);
  const [overrideForm, setOverrideForm] = useState({
    setting_type: "recruiter",
    discount_type: "percent",
    discount_value: "",
    priority: "100",
    note: "",
    is_active: true,
    active_from: "",
    active_to: "",
  });

  const sortBtn = (key, label) => {
    const active = invoiceSort.key === key;
    const dir = active ? invoiceSort.dir : "asc";
    const nextDir = active && dir === "asc" ? "desc" : "asc";
    return (
      <Button
        size="sm"
        color={active ? "primary" : "light"}
        onClick={() => setInvoiceSort({ key, dir: nextDir })}
        className="mr-2"
      >
        {label} {active ? (dir === "asc" ? "↑" : "↓") : ""}
      </Button>
    );
  };

  // Deduplicate hospitals; store canonical status
  const dedupeHospitals = (rows) => {
    const map = new Map();
    for (const r of rows || []) {
      const id = r.hosp_id;
      const name = r.name || r.hosp_name || "";
      const rawStatus = r.subscription_status || r.status || null;
      const entry = map.get(id) || { hosp_id: id, name, statuses: new Set(), has_subscriptions: false };
      if (rawStatus !== null && rawStatus !== undefined) {
        entry.statuses.add(normalizeStatus(rawStatus)); // canonical
        entry.has_subscriptions = true;
      }
      map.set(id, entry);
    }
    const priority = ["active", "trial", "deactive", "canceled", "paid", "unpaid", "overdue", "unknown", "pending"];
    const pick = (set) => priority.find((p) => set.has(p)) || "unknown";
    return Array.from(map.values()).map(e => ({
      hosp_id: e.hosp_id,
      name: e.name,
      subscription_status: pick(e.statuses), // canonical key
      has_subscriptions: e.has_subscriptions
    }));
  };

  // ONLY companies that have at least one subscription; apply text query
  const filteredHospitals = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (hospitals || [])
      .filter(h => h.has_subscriptions)
      .filter(h => q ? (h.name || "").toLowerCase().includes(q) : true);
  }, [hospitals, query]);

  const activeCount = useMemo(
    () => (filteredHospitals || []).filter(h => h.subscription_status === "active").length,
    [filteredHospitals]
  );

  const visibleHospitals = useMemo(() => filteredHospitals.slice(0, perPage), [filteredHospitals, perPage]);

  const sortedInvoices = useMemo(() => {
    const arr = [...invoices];
    arr.sort((a, b) => {
      const { key, dir } = invoiceSort;
      let cmp = 0;
      if (key === "amount") {
        cmp = (a.amount ?? 0) - (b.amount ?? 0);
      } else if (key === "due_date") {
        cmp = new Date(a.due_date).getTime() - new Date(b.due_date).getTime();
      } else if (key === "status") {
        cmp = (normalizeStatus(a.status)).localeCompare(normalizeStatus(b.status));
      }
      return dir === "asc" ? cmp : -cmp;
    });
    return arr;
  }, [invoices, invoiceSort]);

  /* ------------- API ------------- */
  const loadHospitals = useCallback(async () => {
    try {
      setLoadingList(true);
      setListError(null);

      const res = await axios.get("/api2/hospitals");
      const rows = res.data || [];

      const uniqueHospitals = dedupeHospitals(rows);
      setHospitals(uniqueHospitals);

      const firstWithSub = uniqueHospitals.find(h => h.has_subscriptions);
      if (!selected && firstWithSub) {
        setSelected(firstWithSub);
      }
    } catch (e) {
      console.error(e);
      setListError("Fehler beim Laden der Unternehmen.");
      setHospitals([]);
    } finally {
      setLoadingList(false);
    }
  }, [selected]);

  const fetchSubscriptionsPlural = async (hospId) => {
    const res = await axios.get(`/api2/hospitals/${hospId}/subscriptions`);
    return Array.isArray(res.data) ? res.data : [];
  };

  const fetchSubscriptionSingularAsArray = async (hospId) => {
    const res = await axios.get(`/api2/hospitals/${hospId}/subscription`);
    const data = res.data;
    if (!data) return [];
    if (Array.isArray(data)) return data;
    return [data];
  };

  const loadDetails = useCallback(async (hospId) => {
    if (!hospId) return;
    try {
      setLoadingDetail(true);
      setDetailError(null);
      setDetailSuccess(null);

      let subs = [];
      try {
        subs = await fetchSubscriptionsPlural(hospId);
      } catch {
        subs = await fetchSubscriptionSingularAsArray(hospId);
      }

      subs = (subs || []).map(s => ({
        subscription_id: s.subscription_id,
        subscription_type: s.subscription_type,
        status: s.status,
        start_date: s.start_date,
        end_date: s.end_date,
        payment_plan: s.payment_plan,
        monthly_price: s.monthly_price,
        annual_discount_percentage: s.annual_discount_percentage,
        total_amount_paid: s.total_amount_paid,
        cancellation_date: s.cancellation_date,
        created_at: s.created_at,
        updated_at: s.updated_at,
        contract_link_sent_date: s.contract_link_sent_date,
        contract_signed_date: s.contract_signed_date,
        contract_s3_key: s.contract_s3_key,
        contract_content_id: s.contract_content_id,
        invoice_s3_key: s.invoice_s3_key,
        order_number: s.order_number,
        user_id: s.user_id
      }));
      setSubscriptions(subs);

      let invs = [];
      try {
        const invRes = await axios.get(`/api2/hospitals/${hospId}/invoices`);
        invs = invRes.data || [];
      } catch {
        invs = [];
      }
      setInvoices(invs);
    } catch (e) {
      console.error(e);
      setDetailError("Fehler beim Laden der Details.");
      setSubscriptions([]);
      setInvoices([]);
    } finally {
      setLoadingDetail(false);
    }
  }, []);

  const loadOverrides = useCallback(async (hospId) => {
    if (!hospId) return;
    try {
      setLoadingOverrides(true);
      const res = await axios.get(`/api2/hospital-price-overrides?hosp_id=${hospId}`);
      setOverrides(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error(e);
      setDetailError("Fehler beim Laden der Preis-Overrides.");
      setOverrides([]);
    } finally {
      setLoadingOverrides(false);
    }
  }, []);

  const loadOverridePreview = useCallback(async (hospId, settingType) => {
    if (!hospId || !settingType) return;
    try {
      const res = await axios.get(
        `/api2/hospital-price-overrides/preview?hosp_id=${hospId}&setting_type=${encodeURIComponent(
          settingType
        )}`
      );
      setOverridePreview(res.data || null);
    } catch {
      setOverridePreview(null);
    }
  }, []);

  // --- Actions: Subscriptions ---
  // NOTE: Keep backend payloads in English (Active/Deactive/Canceled)
  const updateSubscriptionStatus = async (subscriptionId, nextStatusEn) => {
    if (!subscriptionId || !selected?.hosp_id) return;
    if (!window.confirm(`Status wirklich auf "${statusLabelDE(normalizeStatus(nextStatusEn))}" setzen?`)) return;

    try {
      setDetailError(null);
      setDetailSuccess(null);
      setUpdatingSubId(subscriptionId);

      await axios.patch(`/api2/subscriptions/${subscriptionId}/status`, { status: nextStatusEn });

      setDetailSuccess("Abo-Status aktualisiert.");
      await loadDetails(selected.hosp_id);
    } catch (e) {
      console.error(e);
      setDetailError("Konnte Abo-Status nicht aktualisieren. Bitte Endpoint /api2/subscriptions/:id/status implementieren.");
    } finally {
      setUpdatingSubId(null);
    }
  };

  // --- Actions: Invoices ---
  const sendInvoiceReminder = async (invoiceId) => {
    if (!invoiceId) return;
    try {
      setDetailError(null);
      setDetailSuccess(null);
      setSendingReminderId(invoiceId);

      await axios.post(`/api2/invoices/${invoiceId}/send-reminder`);

      setDetailSuccess("Erinnerungs-E-Mail gesendet.");
    } catch (e) {
      console.error(e);
      setDetailError("Konnte Erinnerungs-E-Mail nicht senden. Bitte Endpoint /api2/invoices/:id/send-reminder implementieren.");
    } finally {
      setSendingReminderId(null);
    }
  };

  const updateInvoiceStatus = async (invoiceId, nextStatus) => {
    if (!invoiceId) return;
    try {
      setDetailError(null);
      setDetailSuccess(null);
      setUpdatingInvoiceId(invoiceId);

      await axios.patch(`/api2/invoices/${invoiceId}/status`, { status: nextStatus });

      setDetailSuccess("Rechnungsstatus aktualisiert.");
      if (selected?.hosp_id) await loadDetails(selected.hosp_id);
    } catch (e) {
      console.error(e);
      setDetailError("Konnte Rechnungsstatus nicht aktualisieren. Bitte Endpoint /api2/invoices/:id/status implementieren.");
    } finally {
      setUpdatingInvoiceId(null);
    }
  };

  const handleDownloadInvoice = async (inv) => {
    const invoiceKey = String(inv?.invoice_s3_key || "").trim();
    if (!invoiceKey) {
      setDetailError("Kein invoice_s3_key für diese Rechnung vorhanden.");
      return;
    }
    try {
      setDownloadingInvoiceId(inv?.invoice_id || null);
      const response = await axios.get(`/api2/download-invoice?filename=${encodeURIComponent(invoiceKey)}`, {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const filename = formatFileName(invoiceKey) || `${inv?.invoice_number || "invoice"}.pdf`;
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      setDetailError('Konnte Rechnung nicht herunterladen.');
    } finally {
      setDownloadingInvoiceId(null);
    }
  };
  const handleDownloadContract = async (contractKey) => {
    if (!contractKey) return;
    try {
      setDetailError(null);
      setDetailSuccess(null);
      setDownloadingContractId(contractKey);
      const response = await axios.get(
        `/api2/download-invoice?filename=${encodeURIComponent(contractKey)}`,
        { responseType: "blob" }
      );
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      const downloadName = formatFileName(contractKey) || "contract.pdf";
      link.href = url;
      link.download = downloadName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      setDetailError("Konnte Vertrag nicht herunterladen.");
    } finally {
      setDownloadingContractId(null);
    }
  };

  /* ------------- effects ------------- */
  useEffect(() => { loadHospitals(); }, [loadHospitals]);
  useEffect(() => { if (selected?.hosp_id) loadDetails(selected.hosp_id); }, [selected, loadDetails]);
  useEffect(() => { if (selected?.hosp_id) loadOverrides(selected.hosp_id); }, [selected, loadOverrides]);
  useEffect(() => {
    if (selected?.hosp_id) {
      loadOverridePreview(selected.hosp_id, overrideForm.setting_type);
    }
  }, [selected, overrideForm.setting_type, loadOverridePreview]);

  /* ------------- actions ------------- */
  const handleCreateInvoice = async () => {
    if (!selected?.hosp_id) return;
    if (!invoiceForm.amount || Number(invoiceForm.amount) <= 0) {
      setDetailError("Bitte einen gültigen Betrag eingeben.");
      return;
    }
    if (!invoiceForm.dueDate) {
      setDetailError("Bitte ein Fälligkeitsdatum wählen.");
      return;
    }
    try {
      setDetailError(null);
      setDetailSuccess(null);
      await axios.post(`/api2/hospitals/${selected.hosp_id}/invoices`, {
        amount: Number(invoiceForm.amount),
        dueDate: invoiceForm.dueDate,
        description: invoiceForm.description,
      });
      setInvoiceModal(false);
      setInvoiceForm({ amount: "", dueDate: "", description: "" });
      await loadDetails(selected.hosp_id);
      setDetailSuccess("Rechnung erstellt.");
    } catch (e) {
      console.error(e);
      setDetailError("Rechnung konnte nicht erstellt werden.");
    }
  };

  const resetOverrideForm = () => {
    setEditingOverrideId(null);
    setOverrideForm({
      setting_type: "recruiter",
      discount_type: "percent",
      discount_value: "",
      priority: "100",
      note: "",
      is_active: true,
      active_from: "",
      active_to: "",
    });
  };

  const handleOverrideSave = async () => {
    if (!selected?.hosp_id) return;
    if (!overrideForm.discount_value || Number(overrideForm.discount_value) < 0) {
      setDetailError("Bitte einen gültigen Rabattwert eingeben.");
      return;
    }
    try {
      setSavingOverride(true);
      setDetailError(null);
      const payload = {
        hosp_id: selected.hosp_id,
        setting_type: overrideForm.setting_type,
        discount_type: overrideForm.discount_type,
        discount_value: Number(overrideForm.discount_value),
        priority: Number(overrideForm.priority) || 100,
        note: overrideForm.note || null,
        is_active: overrideForm.is_active ? 1 : 0,
        active_from: overrideForm.active_from || null,
        active_to: overrideForm.active_to || null,
      };
      if (editingOverrideId) {
        await axios.put(`/api2/hospital-price-overrides/${editingOverrideId}`, payload);
        setDetailSuccess("Preis-Override aktualisiert.");
      } else {
        await axios.post("/api2/hospital-price-overrides", payload);
        setDetailSuccess("Preis-Override erstellt.");
      }
      resetOverrideForm();
      await loadOverrides(selected.hosp_id);
      await loadOverridePreview(selected.hosp_id, payload.setting_type);
    } catch (e) {
      console.error(e);
      setDetailError("Preis-Override konnte nicht gespeichert werden.");
    } finally {
      setSavingOverride(false);
    }
  };

  const startEditOverride = (row) => {
    setEditingOverrideId(row.id);
    setOverrideForm({
      setting_type: row.setting_type || "recruiter",
      discount_type: row.discount_type || "percent",
      discount_value: row.discount_value ?? "",
      priority: row.priority ?? "100",
      note: row.note || "",
      is_active: Number(row.is_active) === 1,
      active_from: row.active_from ? String(row.active_from).slice(0, 16) : "",
      active_to: row.active_to ? String(row.active_to).slice(0, 16) : "",
    });
  };

  const toggleOverride = async (row) => {
    try {
      await axios.patch(`/api2/hospital-price-overrides/${row.id}/toggle`, {
        is_active: Number(row.is_active) === 1 ? 0 : 1,
      });
      await loadOverrides(selected?.hosp_id);
      await loadOverridePreview(selected?.hosp_id, row.setting_type);
    } catch (e) {
      console.error(e);
      setDetailError("Override-Status konnte nicht geändert werden.");
    }
  };

  const deleteOverride = async (row) => {
    if (!window.confirm("Override wirklich löschen?")) return;
    try {
      await axios.delete(`/api2/hospital-price-overrides/${row.id}`);
      await loadOverrides(selected?.hosp_id);
      await loadOverridePreview(selected?.hosp_id, row.setting_type);
    } catch (e) {
      console.error(e);
      setDetailError("Override konnte nicht gelöscht werden.");
    }
  };

  /* ------------- render ------------- */
  return (
    <div className="content">
      <Container fluid>
        {(listError || detailError || detailSuccess) && (
          <Row className="mb-3">
            <Col>
              {listError && <Alert color="danger" toggle={() => setListError(null)}>{listError}</Alert>}
              {detailError && <Alert color="danger" toggle={() => setDetailError(null)}>{detailError}</Alert>}
              {detailSuccess && <Alert color="success" toggle={() => setDetailSuccess(null)}>{detailSuccess}</Alert>}
            </Col>
          </Row>
        )}

        <Row>
          {/* LEFT: Companies list */}
          <Col md="4" className="mb-4">
            <Card className="h-100">
              <CardHeader className="d-flex justify-content-between align-items-center">
                <div>
                  <h5 className="mb-0">Unternehmen</h5>
                </div>
                <div className="d-flex align-items-center">
                  <Badge color="light" className="mr-2">
                    {filteredHospitals.length} Mit Abos
                  </Badge>
                  <Badge color="success">{activeCount} Aktiv</Badge>
                </div>
              </CardHeader>
              <CardBody>
                {/* search */}
                <div className="mb-3">
                  <InputGroup>
                    <InputGroupText>🔎</InputGroupText>
                    <Input
                      placeholder="Unternehmen suchen…"
                      value={query}
                      onChange={(e) => { setQuery(e.target.value); }}
                      onKeyDown={(e) => { if (e.key === "Enter") loadHospitals(); }}
                    />
                    <Button color="secondary" onClick={() => { loadHospitals(); }} className="ml-2">
                      Neu laden
                    </Button>
                  </InputGroup>
                </div>

                <div className="d-flex justify-content-between align-items-center mb-2">
                  <Badge color={filteredHospitals.length > 0 ? "success" : "warning"}>
                    {filteredHospitals.length} Treffer
                  </Badge>
                  <div className="d-flex align-items-center">
                    <Input
                      type="select"
                      bsSize="sm"
                      value={perPage}
                      onChange={(e) => { setPerPage(parseInt(e.target.value, 10)); }}
                      style={{ width: 120 }}
                      title="Zeige erste N"
                    >
                      {[10, 20, 30, 50, 75, 100, 150].map((n) => <option key={n} value={n}>{`Erste ${n}`}</option>)}
                    </Input>
                    <Button
                      size="sm"
                      color="light"
                      className="ml-2"
                      onClick={() => loadHospitals()}
                      disabled={loadingList}
                      title="Liste neu laden"
                    >
                      {loadingList ? <Spinner size="sm" /> : "Neu laden"}
                    </Button>
                  </div>
                </div>

                <div style={{ maxHeight: 520, overflowY: "auto", borderTop: "1px solid #eee" }}>
                  {loadingList ? (
                    <div className="py-4 text-center"><Spinner color="primary" /></div>
                  ) : filteredHospitals.length === 0 ? (
                    <div className="text-muted small py-2">Keine Unternehmen mit Abonnements.</div>
                  ) : (
                    <ListGroup flush>
                      {visibleHospitals.map((hospital) => {
                        const isActive = selected?.hosp_id === hospital.hosp_id;
                        return (
                          <div key={hospital.hosp_id}>
                            <ListGroupItem
                              active={!!isActive}
                              onClick={() => setSelected(hospital)}
                              action
                              className="d-flex justify-content-between align-items-center"
                            >
                              <div className="font-weight-semibold">{hospital.name || "—"}</div>
                              <div className="d-flex align-items-center">
                                <StatusBadge status={hospital.subscription_status} />
                              </div>
                            </ListGroupItem>
                          </div>
                        );
                      })}
                    </ListGroup>
                  )}
                </div>
              </CardBody>
            </Card>
          </Col>

          {/* RIGHT: Subscriptions + Invoices */}
          <Col md="8" className="mb-4">
            {!selected ? (
              <Card><CardBody className="text-muted">Bitte ein Unternehmen auswählen.</CardBody></Card>
            ) : (
              <Card className="h-100">
                <CardHeader className="d-flex justify-content-between align-items-center">
                  <div>
                    <h5 className="mb-0">{selected.name}</h5>
                    <div className="small text-muted">ID: {selected.hosp_id}</div>
                  </div>
                  <div className="d-flex align-items-center">
                    <Button
                      color="light"
                      size="sm"
                      onClick={() => loadDetails(selected.hosp_id)}
                      disabled={loadingDetail}
                      title="Details aktualisieren"
                      className="mr-2"
                    >
                      {loadingDetail ? <Spinner size="sm" /> : "Aktualisieren"}
                    </Button>
                  </div>
                </CardHeader>

                <CardBody>
                  <Nav pills className="hosps-tabs justify-content-center">
                    <NavItem>
                      <NavLink className={activeTab === "1" ? "active" : ""} onClick={() => setActiveTab("1")} style={{ cursor: "pointer" }}>
                        Abonnements
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink className={activeTab === "2" ? "active" : ""} onClick={() => setActiveTab("2")} style={{ cursor: "pointer" }}>
                        Rechnungen
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink className={activeTab === "3" ? "active" : ""} onClick={() => setActiveTab("3")} style={{ cursor: "pointer" }}>
                        Preis-Overrides
                      </NavLink>
                    </NavItem>
                  </Nav>

                  <TabContent activeTab={activeTab} className="mt-3">
                    {/* Subscriptions (plural) */}
                    <TabPane tabId="1">
                      {loadingDetail ? (
                        <div className="py-5 text-center"><Spinner color="primary" /></div>
                      ) : subscriptions.length === 0 ? (
                        <div className="text-muted">Keine Abonnements für dieses Unternehmen gefunden.</div>
                      ) : (
                        <Table responsive hover>
                          <thead className="text-primary">
                            <tr>
                              <th>Abo & Preise</th>
                              <th>Status</th>
                              <th>Laufzeiten</th>
                              <th>Vertrag</th>
                              <th className="text-right">Aktionen</th>
                            </tr>
                          </thead>
                          <tbody>
                            {subscriptions.map((s) => {
                              const busy = updatingSubId === s.subscription_id;
                              return (
                                <tr key={s.subscription_id || `${s.subscription_type}-${s.start_date}-${s.end_date}`}>
                                  <td style={{minWidth: 230}}>
                                    <div className="font-weight-semibold">{s.subscription_type || "—"}</div>
                                    <div className="text-muted small">
                                      Zahlungsplan: {formatPaymentPlan(s.payment_plan)} · Monat: {formatCurrencyOrDash(s.monthly_price)}
                                    </div>
                                    <div className="text-muted small">
                                      Gesamt bezahlt: {formatCurrencyOrDash(s.total_amount_paid)} · Rabatt: {formatPercent(s.annual_discount_percentage)}
                                    </div>
                                    <div className="text-muted small">
                                      Order #{s.order_number || "—"} · User #{s.user_id || "—"}
                                    </div>
                                  </td>
                                  <td style={{minWidth: 160}}>
                                    <StatusBadge status={s.status} />
                                    {s.cancellation_date && (
                                      <div className="text-danger small">
                                        Kündigung ab {formatDate(s.cancellation_date)}
                                      </div>
                                    )}
                                  </td>
                                  <td style={{minWidth: 180}}>
                                    <div><strong>Start:</strong> {formatDate(s.start_date)}</div>
                                    <div><strong>Ende:</strong> {formatDate(s.end_date)}</div>
                                    <div className="text-muted small">Erstellt: {formatDate(s.created_at)}</div>
                                    <div className="text-muted small">Geändert: {formatDate(s.updated_at)}</div>
                                  </td>
                                  <td style={{minWidth: 220}}>
                                    <div><strong>Link gesendet:</strong> {formatDate(s.contract_link_sent_date)}</div>
                                    <div><strong>Signiert:</strong> {formatDate(s.contract_signed_date)}</div>
                                    {s.contract_s3_key && (
                                      <div className="small mb-1" style={{wordBreak: "break-all"}}>
                                        Vertrag S3: {s.contract_s3_key}
                                      </div>
                                    )}
                                    {s.invoice_s3_key && (
                                      <div className="text-muted small" style={{wordBreak: "break-all"}}>
                                        Rechnung S3: {s.invoice_s3_key}
                                      </div>
                                    )}
                                    {s.contract_content_id && (
                                      <div className="text-muted small" style={{wordBreak: "break-all"}}>
                                        Content-ID: {s.contract_content_id}
                                      </div>
                                    )}
                                    {s.contract_s3_key && (
                                      <Button
                                        size="sm"
                                        color="info"
                                        outline
                                        className="mt-2"
                                        disabled={downloadingContractId === s.contract_s3_key}
                                        onClick={() => handleDownloadContract(s.contract_s3_key)}
                                      >
                                        {downloadingContractId === s.contract_s3_key ? (
                                          <Spinner size="sm" />
                                        ) : (
                                          <>Vertrag laden ({formatFileName(s.contract_s3_key)})</>
                                        )}
                                      </Button>
                                    )}
                                  </td>
                                  <td className="text-right">
                                    <Button
                                      size="sm"
                                      color="success"
                                      outline
                                      className="mr-1"
                                      disabled={busy}
                                      onClick={() => updateSubscriptionStatus(s.subscription_id, "active")}
                                      title="Als aktiv setzen"
                                    >
                                      {busy ? <Spinner size="sm" /> : "Aktivieren"}
                                    </Button>
                                    <Button
                                      size="sm"
                                      color="secondary"
                                      outline
                                      className="mr-1"
                                      disabled={busy}
                                      onClick={() => updateSubscriptionStatus(s.subscription_id, "Deactive")}
                                      title="Als deaktiv setzen"
                                    >
                                      {busy ? <Spinner size="sm" /> : "Deaktivieren"}
                                    </Button>
                                    <Button
                                      size="sm"
                                      color="danger"
                                      outline
                                      disabled={busy}
                                      onClick={() => updateSubscriptionStatus(s.subscription_id, "Canceled")}
                                      title="Abo kündigen"
                                    >
                                      {busy ? <Spinner size="sm" /> : "Kündigen"}
                                    </Button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </Table>
                      )}
                    </TabPane>

                    {/* Invoices */}
                    <TabPane tabId="2">
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <div className="d-flex align-items-center">
                          {sortBtn("due_date", "Nach Datum sortieren")}
                          {sortBtn("amount", "Nach Betrag sortieren")}
                          {sortBtn("status", "Nach Status sortieren")}
                        </div>
                        {/* <Button color="success" onClick={() => setInvoiceModal(true)} disabled={loadingDetail}>
                          Neue Rechnung erstellen
                        </Button> */}
                      </div>

                      {loadingDetail ? (
                        <div className="py-4 text-center"><Spinner color="primary" /></div>
                      ) : sortedInvoices.length === 0 ? (
                        <div className="text-muted">Noch keine Rechnungen.</div>
                      ) : (
                        <Table responsive hover>
                          <thead className="text-primary">
                            <tr>
                              <th>Rechnungsnummer</th>
                              <th>Termine</th>
                              <th className="text-right">Betrag</th>
                              <th>Status</th>
                              <th className="text-right">Aktionen</th>
                            </tr>
                          </thead>
                          <tbody>
                            {sortedInvoices.map((inv) => {
                              const sending = sendingReminderId === inv.invoice_id;
                              const updating = updatingInvoiceId === inv.invoice_id;
                              const canDownload = !!inv.invoice_s3_key && normalizeStatus(inv.status) !== 'pending';
                              return (
                                <tr key={inv.invoice_id}>
                                  <td className="font-weight-semibold">{inv.invoice_number}</td>
                                  <td style={{minWidth: 180}}>
                                    <div><strong>Fällig:</strong> {formatDate(inv.due_date)}</div>
                                    <div className="text-muted small">Erstellt: {formatDate(inv.created_at)}</div>
                                    <div className="text-muted small">Geändert: {formatDate(inv.updated_at)}</div>
                                  </td>
                                  <td className="text-right">{formatCurrency(inv.amount)}</td>
                                  <td><StatusBadge status={inv.status} /></td>
                                  <td className="text-right">
                                    <Button
                                      size="sm"
                                      color="info"
                                      outline
                                      className="mr-1"
                                      disabled={downloadingInvoiceId === inv.invoice_id || !canDownload}
                                      onClick={() => canDownload && handleDownloadInvoice(inv)}
                                      title={canDownload ? "Rechnung ansehen" : "Noch nicht verfügbar"}
                                    >
                                      {downloadingInvoiceId === inv.invoice_id ? <Spinner size="sm" /> : 'Ansehen'}
                                    </Button>
                                    <Button
                                      size="sm"
                                      color="warning"
                                      outline
                                      className="mr-1"
                                      disabled={sending}
                                      onClick={() => sendInvoiceReminder(inv.invoice_id)}
                                      title="Erinnerungs-E-Mail senden"
                                    >
                                      {sending ? <Spinner size="sm" /> : "Erinnerung senden"}
                                    </Button>
                                    <Button
                                      size="sm"
                                      color="success"
                                      outline
                                      disabled={updating}
                                      onClick={() => updateInvoiceStatus(inv.invoice_id, "Paid")}
                                      title="Als bezahlt markieren"
                                    >
                                      {updating ? <Spinner size="sm" /> : "Als bezahlt markieren"}
                                    </Button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </Table>
                      )}
                    </TabPane>

                    <TabPane tabId="3">
                      <Card className="mb-3">
                        <CardBody>
                          <Row>
                            <Col md="3">
                              <FormGroup>
                                <Label>Modell</Label>
                                <Input
                                  type="select"
                                  value={overrideForm.setting_type}
                                  onChange={(e) =>
                                    setOverrideForm((f) => ({...f, setting_type: e.target.value}))
                                  }
                                >
                                  <option value="recruiter">Recruiter</option>
                                  <option value="jobslot">Job-Slot</option>
                                  <option value="freelance">Flexible Jobs</option>
                                  <option value="ausbildung">Ausbildung</option>
                                </Input>
                              </FormGroup>
                            </Col>
                            <Col md="2">
                              <FormGroup>
                                <Label>Typ</Label>
                                <Input
                                  type="select"
                                  value={overrideForm.discount_type}
                                  onChange={(e) =>
                                    setOverrideForm((f) => ({...f, discount_type: e.target.value}))
                                  }
                                >
                                  <option value="percent">Percent</option>
                                  <option value="fixed">Fixed</option>
                                </Input>
                              </FormGroup>
                            </Col>
                            <Col md="2">
                              <FormGroup>
                                <Label>Wert</Label>
                                <Input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={overrideForm.discount_value}
                                  onChange={(e) =>
                                    setOverrideForm((f) => ({...f, discount_value: e.target.value}))
                                  }
                                />
                              </FormGroup>
                            </Col>
                            <Col md="2">
                              <FormGroup>
                                <Label>Priorität</Label>
                                <Input
                                  type="number"
                                  value={overrideForm.priority}
                                  onChange={(e) =>
                                    setOverrideForm((f) => ({...f, priority: e.target.value}))
                                  }
                                />
                              </FormGroup>
                            </Col>
                            <Col md="3" className="d-flex align-items-end">
                              <FormGroup check className="mb-3">
                                <Label check>
                                  <Input
                                    type="checkbox"
                                    checked={overrideForm.is_active}
                                    onChange={(e) =>
                                      setOverrideForm((f) => ({...f, is_active: e.target.checked}))
                                    }
                                  />{" "}
                                  Aktiv
                                </Label>
                              </FormGroup>
                            </Col>
                          </Row>
                          <Row>
                            <Col md="4">
                              <FormGroup>
                                <Label>Aktiv von</Label>
                                <Input
                                  type="datetime-local"
                                  value={overrideForm.active_from}
                                  onChange={(e) =>
                                    setOverrideForm((f) => ({...f, active_from: e.target.value}))
                                  }
                                />
                              </FormGroup>
                            </Col>
                            <Col md="4">
                              <FormGroup>
                                <Label>Aktiv bis</Label>
                                <Input
                                  type="datetime-local"
                                  value={overrideForm.active_to}
                                  onChange={(e) =>
                                    setOverrideForm((f) => ({...f, active_to: e.target.value}))
                                  }
                                />
                              </FormGroup>
                            </Col>
                            <Col md="4">
                              <FormGroup>
                                <Label>Notiz</Label>
                                <Input
                                  value={overrideForm.note}
                                  onChange={(e) =>
                                    setOverrideForm((f) => ({...f, note: e.target.value}))
                                  }
                                />
                              </FormGroup>
                            </Col>
                          </Row>
                          <div className="d-flex align-items-center">
                            <Button color="primary" onClick={handleOverrideSave} disabled={savingOverride}>
                              {savingOverride ? <Spinner size="sm" /> : editingOverrideId ? "Aktualisieren" : "Anlegen"}
                            </Button>
                            <Button color="secondary" outline className="ml-2" onClick={resetOverrideForm}>
                              Zurücksetzen
                            </Button>
                            {overridePreview && (
                              <Badge color="info" className="ml-3">
                                Basis: {formatCurrency(overridePreview.basePriceRate)} | Effektiv:{" "}
                                {formatCurrency(overridePreview.priceRate)}
                              </Badge>
                            )}
                          </div>
                        </CardBody>
                      </Card>

                      {loadingOverrides ? (
                        <div className="py-4 text-center"><Spinner color="primary" /></div>
                      ) : overrides.length === 0 ? (
                        <div className="text-muted">Keine Overrides für dieses Unternehmen.</div>
                      ) : (
                        <Table responsive hover>
                          <thead className="text-primary">
                            <tr>
                              <th>ID</th>
                              <th>Modell</th>
                              <th>Rabatt</th>
                              <th>Priorität</th>
                              <th>Status</th>
                              <th>Aktiv von/bis</th>
                              <th>Notiz</th>
                              <th className="text-right">Aktionen</th>
                            </tr>
                          </thead>
                          <tbody>
                            {overrides.map((o) => (
                              <tr key={o.id}>
                                <td>{o.id}</td>
                                <td>{o.setting_type}</td>
                                <td>
                                  {o.discount_type === "percent"
                                    ? `${o.discount_value}%`
                                    : formatCurrency(o.discount_value)}
                                </td>
                                <td>{o.priority}</td>
                                <td>
                                  <Badge color={Number(o.is_active) === 1 ? "success" : "secondary"}>
                                    {Number(o.is_active) === 1 ? "Aktiv" : "Inaktiv"}
                                  </Badge>
                                </td>
                                <td>
                                  {formatDate(o.active_from)} - {formatDate(o.active_to)}
                                </td>
                                <td>{o.note || "—"}</td>
                                <td className="text-right">
                                  <Button size="sm" color="info" outline className="mr-1" onClick={() => startEditOverride(o)}>
                                    Bearbeiten
                                  </Button>
                                  <Button size="sm" color="warning" outline className="mr-1" onClick={() => toggleOverride(o)}>
                                    {Number(o.is_active) === 1 ? "Deaktivieren" : "Aktivieren"}
                                  </Button>
                                  <Button size="sm" color="danger" outline onClick={() => deleteOverride(o)}>
                                    Löschen
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      )}
                    </TabPane>
                  </TabContent>
                </CardBody>
              </Card>
            )}
          </Col>
        </Row>
      </Container>

      {/* Create Invoice Modal */}
      <Modal isOpen={invoiceModal} toggle={() => setInvoiceModal((v) => !v)}>
        <ModalHeader toggle={() => setInvoiceModal((v) => !v)}>
          Rechnung erstellen{selected ? ` – ${selected.name}` : ""}
        </ModalHeader>
        <ModalBody>
          <Form onSubmit={(e) => { e.preventDefault(); handleCreateInvoice(); }}>
            <FormGroup>
              <Label htmlFor="invAmt">Betrag</Label>
              <Input
                type="number"
                id="invAmt"
                placeholder="z. B. 500"
                value={invoiceForm.amount}
                onChange={(e) => setInvoiceForm((f) => ({ ...f, amount: e.target.value }))}
                min="0"
                step="0.01"
              />
            </FormGroup>
            <FormGroup>
              <Label htmlFor="invDue">Fälligkeitsdatum</Label>
              <Input
                type="date"
                id="invDue"
                value={invoiceForm.dueDate}
                onChange={(e) => setInvoiceForm((f) => ({ ...f, dueDate: e.target.value }))}
              />
            </FormGroup>
            <FormGroup>
              <Label htmlFor="invDesc">Beschreibung</Label>
              <Input
                type="textarea"
                id="invDesc"
                placeholder="z. B. Monatliches Abo – Premium-Tarif"
                value={invoiceForm.description}
                onChange={(e) => setInvoiceForm((f) => ({ ...f, description: e.target.value }))}
                rows={3}
              />
            </FormGroup>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={handleCreateInvoice}>Rechnung erzeugen</Button>
          <Button color="secondary" onClick={() => setInvoiceModal(false)}>Abbrechen</Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default CompanySubscriptions;
